import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Translate from 'components/Translate';
import Input from "../../../../../../../components-new/Input";
import {getBotStatisticNumbers} from '../../../../../../../services/statistics/api';
import LoadingComponent from "../../../../../../../assets/LoadingComponent";
import ExcelButton from "../../../../../../../components-new/buttons/ExcelButton";
import ResetButton from "../../../../../../../components-new/buttons/ResetButton";
import TableHeaderSort from "../../../../../../../components-new/Table/TableHeaderSort";
import {globalExportToExcel} from "../../../../../../../utils/General";

const BotPlatformRequestTable = ({
                                     company,
                                     onCellClick,
                                 }) => {

    const subs = company.subs ? company.subs : [];

    const [filters, setFilters] = useState({
        companyIds: subs.map((sub) => sub.id),
        start: '',
        end: ''
    })
    const [pageable, setPageable] = useState({sort: ''});
    const [bots, setBots] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const sortedMessengers = [...company.tariff.messengers].sort((a, b) => {
        if (a.toLowerCase() === "webchat") return -1;
        if (b.toLowerCase() === "webchat") return 1;
        return 0;
    });

    useEffect(() => {
        fetchData();
    }, [pageable.sort, filters]);

    function updateFilter(key, val) {
        setFilters({
            ...filters,
            [key]: val
        })
    }

    function fetchData() {
        setIsFetching(true);

        getBotStatisticNumbers(filters, pageable).then((res) => {
            if (res) {
                setBots(res);
            } else {
                setBots([]);
            }
        }).catch(e => {
            setBots([])
        }).finally(() => {
            setIsFetching(false);
        })
    }

    function handleSort(column) {
        if (!column) return

        const sort = pageable.sort;
        let newSort
        let currentColumn, currentOrder

        if (sort) {
            currentOrder = sort.split(',').pop();
            currentColumn = sort.split(',').slice(0, -1).join(',');
        }

        if (currentOrder && currentColumn === column) {
            newSort = column + ',' + (currentOrder === 'asc' ? 'desc' : 'asc');
        } else {
            newSort = column + ',' + 'asc'
        }

        setPageable({
            ...pageable,
            sort: newSort,
        });

    }

    function calcCellWidth() {
        return 100 / (company.tariff.messengers.length + 1) + '%'
    }

    function resetFilters() {
        setFilters({
            ...filters,
            start: '',
            end: ''
        })
    }

    const exportToExcel = () => {
        const columns = [
            {label: 'Bot', value: 'bot'},
            ...sortedMessengers.map(messenger => ({
                label: messenger,
                value: messenger,
            })),
        ];

        const data = bots.map(botNumbers => {
            const row = {bot: botNumbers.name};

            sortedMessengers.forEach(messenger => {
                row[messenger] = botNumbers[messenger.toLowerCase()];
            });

            return row;
        });

        globalExportToExcel({
            data,
            columns,
            fileName: 'Bot-Platform requests (' + filters.start + ' ' + filters.end + ').xlsx'
        });
    };

    return (
        <React.Fragment>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                flexWrap:'wrap'
            }}>
                <div style={{display: 'flex', gap: '10px'}}>
                    <Input
                        className={'calendar-select'}
                        style={{width: '240px', height: '40px'}}
                        type="date"
                        value={filters.start}
                        onChange={e => updateFilter('start', e.target.value)}
                    />
                    <Input
                        className={'calendar-select'}
                        style={{width: '240px', height: '40px'}}
                        type="date"
                        value={filters.end}
                        onChange={e => updateFilter('end', e.target.value)}
                    />
                    {(
                        (filters.start !== '' || filters.end !== '') &&
                        <ResetButton
                            onClick={resetFilters}
                            width={150}
                            height={40}
                            variant="outline"
                        />
                    )}
                </div>
                <div>
                    <ExcelButton
                        onClick={exportToExcel}
                        width={190}
                        height={40}
                        variant="outline"
                        children={
                            <span style={{fontSize: '14px', backgroundColor: 'transparent'}}>
                                <Translate text={'downloadXls'}/>
                            </span>
                        }
                    />
                </div>
            </div>

            <br/>

            <div style={{overflowX: 'auto', maxHeight: '600px', width: '100%', minHeight: '200px'}}>
                <table className="custom-table">
                    <thead>
                    <tr>
                        <th style={{textAlign: 'left', width: calcCellWidth()}} onClick={() => handleSort('name')}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <Translate text="bot"/>
                                <TableHeaderSort
                                    isActive={pageable.sort.split(',').slice(0, -1).join(',') === 'name'}
                                    direction={pageable.sort.split(',').pop()}
                                />
                            </div>
                        </th>
                        {sortedMessengers.map(val => (
                            <th key={val} style={{textAlign: 'center', width: calcCellWidth()}}
                                onClick={() => handleSort(val.toLowerCase())}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center'}}>
                                    <Translate text={val}/>
                                    <TableHeaderSort
                                        isActive={pageable.sort.split(',').slice(0, -1).join(',') === val.toLowerCase()}
                                        direction={pageable.sort.split(',').pop()}
                                    />
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>
                    {
                        !isFetching &&
                        <tbody>
                        {
                            bots.map((bot) => (
                                <tr key={bot.id}>
                                    <td style={{textAlign: 'left'}}>{bot.name}</td>
                                    {sortedMessengers.map(messenger => (
                                        <td
                                            key={bot.id + messenger}
                                            className="clickable-td"
                                            style={{textAlign: 'center', cursor: 'pointer'}}
                                            onClick={() => onCellClick(messenger, {bot: bot.id, platforms: messenger, end: filters.end, start: filters.start})}
                                        >
                                            {bot[messenger.toLowerCase()]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        }

                        {
                            bots.length === 0 && !isFetching &&
                            <tr>
                                <td colSpan={sortedMessengers.length + 1}
                                    style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}>
                                    <Translate text={'noResultsFound'}/>
                                </td>
                            </tr>
                        }
                        </tbody>
                    }

                </table>
                {
                    isFetching
                    &&
                    <div style={{height: '150px'}}>
                        <LoadingComponent/>
                    </div>
                }
            </div>
        </React.Fragment>
    );
};

BotPlatformRequestTable.propTypes = {
    company: PropTypes.object.isRequired,
    onCellClick: PropTypes.func.isRequired,
};

export default BotPlatformRequestTable;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/chats/BotPlatformRequestTable.jsx